import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { apiRequest } from '../../services/api/axiosConfig';
import { User, AIAgent, Category, OrganizationUsageData } from '../../types';
import {
  Organization,
  OrganizationMember,
  OrganizationAgent,
  CreditWalletInfo,
  toggleOrganizationActive as apiToggleOrganizationActive
} from '../../services/api/adminOrganizationService';
import categoryService, { CategoryCreateRequest, CategoryUpdateRequest } from '../../services/api/categoryService';
import adminImageStyleService, {
  AdminImageStyle,
  ImageStyleCreateRequest,
  ImageStyleUpdateRequest
} from '../../services/api/adminImageStyleService';
import adminVideoTemplateService, {
  AdminVideoTemplate,
  VideoTemplateCreateRequest,
  VideoTemplateUpdateRequest
} from '../../services/api/adminVideoTemplateService';
import imagePromptTemplateService, {
  ImagePromptTemplate,
  ImagePromptTemplateCreateRequest,
  ImagePromptTemplateUpdateRequest
} from '../../services/api/imagePromptTemplateService';

// APIレスポンス型
interface UsersResponse {
  data: {
    users: User[];
  };
}

interface UserResponse {
  data: {
    user: User;
  };
}

/**
 * 管理画面でユーザーを保存したとき、backend が
 * `firebase_account_creation_required` と一緒に返してくる情報
 * （Admin/UserManagementController::firebaseCreationInfo）。
 *
 * `password` は平文の初期パスワードで、無い場合は null になり
 * `password_available: false` が付く。以前 backend は password が無いときに
 * 'TempPassword123!' という固定値を返しており、全ユーザー共通の既知パスワードで
 * Firebase アカウントが作られる穴になっていた。password が無い場合は Firebase
 * 作成を試みず、パスワード設定リンクで本人に設定してもらう運用にすること。
 */
export interface AdminFirebaseCreationInfo {
  user_id: number;
  email: string;
  name: string;
  password: string | null;
  /** 平文パスワードを返せたかどうか。false なら Firebase 作成は不可。 */
  password_available?: boolean;
}

interface AgentsResponse {
  data: {
    agents: AIAgent[];
  };
}

interface AgentResponse {
  data: {
    agent: AIAgent;
  };
}

interface AgentUsageStatsResponse {
  data: {
    agent_stats: {
      id: number;
      title: string;
      is_active: boolean;
      chat_count: number;
      user_count: number;
    }[];
    daily_stats: {
      agent_name: string;
      date: string;
      tokens_used: number;
      unique_users: number;
    }[];
  };
}

// 組織のAPIレスポンス型
interface OrganizationsResponse {
  data: {
    organizations: Organization[];
  };
}

interface OrganizationResponse {
  data: {
    organization: Organization;
  };
}

interface OrganizationDetailsResponse {
  data: {
    organization: Organization;
    members: OrganizationMember[];
    agents: OrganizationAgent[];
    credit_wallets?: CreditWalletInfo;
    total_credit_balance?: number;
  };
}

interface OrganizationUsageResponse {
  data: OrganizationUsageData;
}

// 管理者統計の型定義
interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  totalChats: number;
  totalMessages: number;
  averageMessagesPerChat: number;
  dailyActiveUsers: { date: string; count: number }[];
}

// AIエージェント統計の型定義
interface AIAgentUsageStats {
  agent_stats: {
    id: number;
    title: string;
    is_active: boolean;
    chat_count: number;
    user_count: number;
  }[];
  daily_stats: {
    agent_name: string;
    date: string;
    tokens_used: number;
    unique_users: number;
  }[];
}

// 管理者状態の型定義
interface AdminState {
  users: User[];
  agents: AIAgent[];
  stats: AdminStats | null;
  agentUsageStats: AIAgentUsageStats | null;
  selectedUser: User | null;
  selectedAgent: AIAgent | null;
  
  // 組織関連の状態
  organizations: Organization[];
  selectedOrganization: Organization | null;
  organizationDetails: OrganizationDetailsResponse['data'] | null;
  organizationUsageData: OrganizationUsageData | null;
  usageDataLoading: boolean;
  usageDataError: string | null;
  
  // カテゴリ関連の状態
  categories: Category[];
  selectedCategory: Category | null;

  // 画像スタイル関連の状態
  imageStyles: AdminImageStyle[];
  selectedImageStyle: AdminImageStyle | null;

  // プロンプトテンプレート関連の状態
  imagePromptTemplates: ImagePromptTemplate[];
  selectedImagePromptTemplate: ImagePromptTemplate | null;

  // 動画テンプレート関連の状態
  videoTemplates: AdminVideoTemplate[];
  selectedVideoTemplate: AdminVideoTemplate | null;

  isLoading: boolean;
  error: string | null;
}

// 初期状態
const initialState: AdminState = {
  users: [],
  agents: [],
  stats: null,
  agentUsageStats: null,
  selectedUser: null,
  selectedAgent: null,
  
  // 組織関連の初期状態
  organizations: [],
  selectedOrganization: null,
  organizationDetails: null,
  organizationUsageData: null,
  usageDataLoading: false,
  usageDataError: null,
  
  // カテゴリ関連の初期状態
  categories: [],
  selectedCategory: null,

  // 画像スタイル関連の初期状態
  imageStyles: [],
  selectedImageStyle: null,

  // プロンプトテンプレート関連の初期状態
  imagePromptTemplates: [],
  selectedImagePromptTemplate: null,

  // 動画テンプレート関連の初期状態
  videoTemplates: [],
  selectedVideoTemplate: null,

  isLoading: false,
  error: null
};

// 管理者向けのユーザー一覧取得アクション
export const fetchUsers = createAsyncThunk(
  'admin/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get<UsersResponse>('admin/users');
      return response.data.users;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'ユーザー情報の取得に失敗しました');
    }
  }
);

// 管理者向けのAIエージェント一覧取得アクション
export const fetchAgents = createAsyncThunk(
  'admin/fetchAgents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get<AgentsResponse>('admin/ai-agents');
      return response.data.agents;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'AIエージェント情報の取得に失敗しました');
    }
  }
);

// 管理者向けの統計情報取得アクション
export const fetchAdminStats = createAsyncThunk(
  'admin/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get<AdminStats>('admin/stats');
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '統計情報の取得に失敗しました');
    }
  }
);

// AIエージェントの使用統計情報取得アクション
export const fetchAgentUsageStats = createAsyncThunk(
  'admin/fetchAgentUsageStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get<AgentUsageStatsResponse>('admin/ai-agents/usage-stats');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'エージェント使用統計の取得に失敗しました');
    }
  }
);

// ユーザー作成アクション
export const createUser = createAsyncThunk(
  'admin/createUser',
  async (userData: { name: string; email: string; password: string; token_limit?: number }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post<UserResponse>('admin/users', userData);
      return response.data.user;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'ユーザー作成に失敗しました');
    }
  }
);

// ユーザー更新アクション
export const updateUser = createAsyncThunk(
  'admin/updateUser',
  async ({ id, userData }: { id: number; userData: Partial<User> }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.put(`admin/users/${id}`, userData);
      // レスポンス全体を返す（firebase_account_creation_requiredフラグを含む）
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'ユーザー更新に失敗しました');
    }
  }
);

// ユーザー削除アクション
export const deleteUser = createAsyncThunk(
  'admin/deleteUser',
  async (userId: number, { rejectWithValue }) => {
    try {
      await apiRequest.delete(`admin/users/${userId}`);
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'ユーザー削除に失敗しました');
    }
  }
);

// AIエージェント作成アクション
export const createAgent = createAsyncThunk(
  'admin/createAgent',
  async (agentData: { title: string; description?: string; model_type: string; prompt_template: string; is_active?: boolean; category?: string; base_color?: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post<AgentResponse>('admin/ai-agents', agentData);
      return response.data.agent;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'AIエージェント作成に失敗しました');
    }
  }
);

// AIエージェント更新アクション
export const updateAgent = createAsyncThunk(
  'admin/updateAgent',
  async ({ id, agentData }: { id: number; agentData: Partial<AIAgent> }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.put<AgentResponse>(`admin/ai-agents/${id}`, agentData);
      return response.data.agent;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'AIエージェント更新に失敗しました');
    }
  }
);

// AIエージェント削除アクション
export const deleteAgent = createAsyncThunk(
  'admin/deleteAgent',
  async (agentId: number, { rejectWithValue }) => {
    try {
      await apiRequest.delete(`admin/ai-agents/${agentId}`);
      return agentId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'AIエージェント削除に失敗しました');
    }
  }
);

// 組織一覧取得アクション
export const fetchOrganizations = createAsyncThunk(
  'admin/fetchOrganizations',
  async (_, { rejectWithValue }) => {
    try {
      console.log('fetchOrganizations: APIリクエスト開始');
      // apiRequest.get の戻り値に型を指定
      const response = await apiRequest.get<{ data: { organizations: Organization[] } }>('admin/organizations');
      console.log('fetchOrganizations: APIレスポンス受信', response);
      
      // レスポンスの構造を確認
      if (response && response.data && response.data.organizations) {
        console.log('fetchOrganizations: 組織データ取得成功', response.data.organizations);
        return response.data.organizations;
      } else {
        console.error('fetchOrganizations: レスポンス構造が期待と異なります', response);
        return rejectWithValue('APIのレスポンス形式が期待と異なります');
      }
    } catch (error: any) {
      console.error('fetchOrganizations: エラー発生', error);
      console.error('fetchOrganizations: エラーレスポンス', error.response);
      return rejectWithValue(error.response?.data?.message || '組織一覧の取得に失敗しました');
    }
  }
);

// 組織詳細取得アクション
export const fetchOrganizationDetails = createAsyncThunk(
  'admin/fetchOrganizationDetails',
  async (organizationId: number, { rejectWithValue }) => {
    try {
      console.log('fetchOrganizationDetails: APIリクエスト開始', organizationId);
      
      // APIからのレスポンス取得（より詳細なデバッグ情報）
      const response = await apiRequest.get(`admin/organizations/${organizationId}`);
      // 型の問題に対処するためにany型にキャスト 
      const typedResponse = response as any;
      console.log('fetchOrganizationDetails: APIレスポンス受信 (完全な内容)', JSON.stringify(typedResponse));
      
      // レスポンス構造の段階的検証
      if (!typedResponse) {
        console.error('fetchOrganizationDetails: レスポンスがありません');
        return rejectWithValue('レスポンスが存在しません');
      }

      // response.data が存在するか確認
      if (!typedResponse.data) {
        console.error('fetchOrganizationDetails: response.data がありません', typedResponse);
        return rejectWithValue('response.dataが存在しません');
      }
      
      // data.data が存在するか確認（APIのネストされたレスポンス構造）
      if (typedResponse.data.data) {
        console.log('fetchOrganizationDetails: data.data 構造を検出', typedResponse.data.data);
        const apiData = typedResponse.data.data;
        
        // data.data 内の必須フィールドを確認
        if (!apiData.organization || !apiData.members || !apiData.agents) {
          const missing = [];
          if (!apiData.organization) missing.push('organization');
          if (!apiData.members) missing.push('members');
          if (!apiData.agents) missing.push('agents');

          console.error('fetchOrganizationDetails: data.data内の必須フィールドがありません', missing, apiData);
          return rejectWithValue(`APIレスポンスに必須フィールドがありません: ${missing.join(', ')}`);
        }

        // 正しい構造で返す
        return {
          organization: apiData.organization as Organization,
          members: apiData.members as OrganizationMember[],
          agents: apiData.agents as OrganizationAgent[],
          credit_wallets: apiData.credit_wallets,
          total_credit_balance: apiData.total_credit_balance,
        };
      }
      // data 直下に必要なフィールドがあるか確認
      else if (typedResponse.data.organization || typedResponse.data.members || typedResponse.data.agents) {
        console.log('fetchOrganizationDetails: data 直下に構造を検出', typedResponse.data);

        // 必須フィールドの確認
        if (!typedResponse.data.organization || !typedResponse.data.members || !typedResponse.data.agents) {
          const missing = [];
          if (!typedResponse.data.organization) missing.push('organization');
          if (!typedResponse.data.members) missing.push('members');
          if (!typedResponse.data.agents) missing.push('agents');

          console.error('fetchOrganizationDetails: data内の必須フィールドがありません', missing, typedResponse.data);
          return rejectWithValue(`APIレスポンスに必須フィールドがありません: ${missing.join(', ')}`);
        }

        // 正しい構造で返す
        return {
          organization: typedResponse.data.organization as Organization,
          members: typedResponse.data.members as OrganizationMember[],
          agents: typedResponse.data.agents as OrganizationAgent[],
          credit_wallets: typedResponse.data.credit_wallets,
          total_credit_balance: typedResponse.data.total_credit_balance,
        };
      } 
      else {
        console.error('fetchOrganizationDetails: 想定外のレスポンス構造', typedResponse);
        return rejectWithValue('APIレスポンスの構造が想定と異なります');
      }
    } catch (error: any) {
      console.error('fetchOrganizationDetails: エラー発生', error);
      return rejectWithValue(error.response?.data?.message || '組織詳細の取得に失敗しました');
    }
  }
);

// 組織使用統計取得アクション
export const fetchOrganizationUsageStatistics = createAsyncThunk(
  'admin/fetchOrganizationUsageStatistics',
  async ({ organizationId, period }: { organizationId: number; period: number }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get(`admin/organizations/${organizationId}/usage-statistics`, {
        params: { period }
      });
      
      // APIレスポンスの構造に合わせて型アサーションを使用
      const responseData = response as unknown as OrganizationUsageResponse;
      return responseData.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '使用統計データの取得に失敗しました');
    }
  }
);

// 組織作成アクション
export const createOrganization = createAsyncThunk(
  'admin/createOrganization',
  async (data: { name: string; description?: string; member_limit: number; monthly_token_limit: number; max_concurrent_image_jobs?: number }, { rejectWithValue }) => {
    try {
      // apiRequest は axios の response.data（= body）を返す。
      // backend（Admin\OrganizationManagementController::store）の body は { message, data: { organization } }。
      const response = await apiRequest.post<{ data: { organization: Organization } }>('admin/organizations', data);
      return response.data.organization;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '組織の作成に失敗しました');
    }
  }
);

// 組織更新アクション
export const updateOrganization = createAsyncThunk(
  'admin/updateOrganization',
  async ({ id, data }: { id: number; data: Partial<Organization> }, { rejectWithValue }) => {
    try {
      console.log('updateOrganization: APIリクエスト開始', id, data);
      const response = await apiRequest.put(`admin/organizations/${id}`, data);
      console.log('updateOrganization: APIレスポンス受信', JSON.stringify(response));
      
      // レスポンス構造を確認し、異なるパターンに対応
      const anyResponse = response as any;
      
      // パターン1: response.data.data.organization
      if (anyResponse?.data?.data?.organization) {
        console.log('updateOrganization: パターン1のレスポンス構造', anyResponse.data.data.organization);
        return anyResponse.data.data.organization;
      }
      
      // パターン2: response.data.organization
      else if (anyResponse?.data?.organization) {
        console.log('updateOrganization: パターン2のレスポンス構造', anyResponse.data.organization);
        return anyResponse.data.organization;
      }
      
      // パターン3: response.data 自体がOrganizationオブジェクト
      else if (anyResponse?.data?.id && anyResponse?.data?.name) {
        console.log('updateOrganization: パターン3のレスポンス構造', anyResponse.data);
        return anyResponse.data;
      }
      
      // パターン4: responseがOrganizationのデータを直接含む
      else if (anyResponse?.id && anyResponse?.name) {
        console.log('updateOrganization: パターン4のレスポンス構造', anyResponse);
        return anyResponse;
      }
      
      // 想定外のレスポンス構造
      console.error('updateOrganization: 想定外のレスポンス構造', anyResponse);
      return rejectWithValue('APIレスポンスの構造が想定と異なります');
    } catch (error: any) {
      console.error('updateOrganization: エラー発生', error);
      return rejectWithValue(error.response?.data?.message || '組織の更新に失敗しました');
    }
  }
);

// 組織削除アクション
export const deleteOrganization = createAsyncThunk(
  'admin/deleteOrganization',
  async (organizationId: number, { rejectWithValue }) => {
    try {
      await apiRequest.delete(`admin/organizations/${organizationId}`);
      return organizationId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '組織の削除に失敗しました');
    }
  }
);

// 組織メンバー追加・更新アクション
export const addOrUpdateOrganizationMember = createAsyncThunk(
  'admin/addOrUpdateOrganizationMember',
  async ({
    organizationId,
    data
  }: {
    organizationId: number;
    data: {
      user_id?: number;
      name: string;
      email: string;
      role: 'leader' | 'member';
      password?: string;
    };
  }, { rejectWithValue }) => {
    try {
      console.log('addOrUpdateOrganizationMember: APIリクエスト開始', organizationId, data);
      const response = await apiRequest.post(`admin/organizations/${organizationId}/members`, data);
      console.log('addOrUpdateOrganizationMember: APIレスポンス受信', JSON.stringify(response));

      // レスポンス構造を確認し、異なるパターンに対応
      const anyResponse = response as any;

      // パターン1: response.data.data.member
      if (anyResponse?.data?.data?.member) {
        console.log('addOrUpdateOrganizationMember: パターン1のレスポンス構造', anyResponse.data.data.member);
        return {
          organizationId,
          member: anyResponse.data.data.member,
          data: anyResponse.data.data
        };
      }

      // パターン2: response.data.member
      else if (anyResponse?.data?.member) {
        console.log('addOrUpdateOrganizationMember: パターン2のレスポンス構造', anyResponse.data.member);
        return {
          organizationId,
          member: anyResponse.data.member,
          data: anyResponse.data
        };
      }

      // パターン3: response.data 自体がmemberオブジェクト
      else if (anyResponse?.data?.id && anyResponse?.data?.email) {
        console.log('addOrUpdateOrganizationMember: パターン3のレスポンス構造', anyResponse.data);
        return {
          organizationId,
          member: anyResponse.data,
          data: anyResponse
        };
      }

      // パターン4: responseがmemberのデータを直接含む
      else if (anyResponse?.id && anyResponse?.email) {
        console.log('addOrUpdateOrganizationMember: パターン4のレスポンス構造', anyResponse);
        return {
          organizationId,
          member: anyResponse,
          data: { member: anyResponse }
        };
      }

      // パターン5: 招待レスポンス（response.data.invitation）
      else if (anyResponse?.data?.invitation) {
        console.log('addOrUpdateOrganizationMember: 招待レスポンス構造', anyResponse.data.invitation);
        // 招待の場合は、招待情報を返す（メンバーリストの更新は別途行う）
        return {
          organizationId,
          invitation: anyResponse.data.invitation,
          isInvitation: true
        };
      }

      // 想定外のレスポンス構造
      console.error('addOrUpdateOrganizationMember: 想定外のレスポンス構造', anyResponse);
      return rejectWithValue('APIレスポンスの構造が想定と異なります');
    } catch (error: any) {
      console.error('addOrUpdateOrganizationMember: エラー発生', error);
      return rejectWithValue(error.response?.data?.message || 'メンバーの追加・更新に失敗しました');
    }
  }
);

// 組織メンバー削除アクション
export const removeOrganizationMember = createAsyncThunk(
  'admin/removeOrganizationMember',
  async ({ organizationId, userId }: { organizationId: number; userId: number }, { rejectWithValue }) => {
    try {
      await apiRequest.delete(`admin/organizations/${organizationId}/members/${userId}`);
      return { organizationId, userId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'メンバーの削除に失敗しました');
    }
  }
);

// 組織メンバーにログイン情報メールを送信
export const sendMemberLoginEmail = createAsyncThunk(
  'admin/sendMemberLoginEmail',
  async ({ organizationId, userId }: { organizationId: number; userId: number }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(`admin/organizations/${organizationId}/members/${userId}/send-login-email`);
      return { organizationId, userId, message: (response as any)?.message || 'メールを送信しました' };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'ログイン情報メールの送信に失敗しました');
    }
  }
);

// 組織のAIエージェント設定更新アクション
export const updateOrganizationAgentSettings = createAsyncThunk(
  'admin/updateOrganizationAgentSettings',
  async ({
    organizationId,
    settings
  }: {
    organizationId: number;
    settings: {
      agent_settings: {
        agent_id: number;
        is_enabled: boolean;
      }[];
    };
  }, { rejectWithValue }) => {
    try {
      await apiRequest.put(`admin/organizations/${organizationId}/agent-settings`, settings);
      return {
        organizationId,
        agentSettings: settings.agent_settings
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'AIエージェント設定の更新に失敗しました');
    }
  }
);

// 組織の有効/無効を切り替えるアクション
export const toggleOrganizationActive = createAsyncThunk(
  'admin/toggleOrganizationActive',
  async ({ id, isActive }: { id: number, isActive: boolean }, { rejectWithValue }) => {
    try {
      const response = await apiToggleOrganizationActive(id, isActive);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 
        (isActive ? '組織の有効化に失敗しました' : '組織の無効化に失敗しました'));
    }
  }
);

// カテゴリ一覧取得アクション
export const fetchCategories = createAsyncThunk(
  'admin/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const categories = await categoryService.getAllCategories();
      return categories;
    } catch (error: any) {
      return rejectWithValue(error.message || 'カテゴリ一覧の取得に失敗しました');
    }
  }
);

// カテゴリ作成アクション
export const createCategory = createAsyncThunk(
  'admin/createCategory',
  async (categoryData: CategoryCreateRequest, { rejectWithValue }) => {
    try {
      const category = await categoryService.createCategory(categoryData);
      return category;
    } catch (error: any) {
      return rejectWithValue(error.message || 'カテゴリの作成に失敗しました');
    }
  }
);

// カテゴリ更新アクション
export const updateCategory = createAsyncThunk(
  'admin/updateCategory',
  async ({ id, categoryData }: { id: number; categoryData: CategoryUpdateRequest }, { rejectWithValue }) => {
    try {
      const category = await categoryService.updateCategory(id, categoryData);
      return category;
    } catch (error: any) {
      return rejectWithValue(error.message || 'カテゴリの更新に失敗しました');
    }
  }
);

// カテゴリ削除アクション
export const deleteCategory = createAsyncThunk(
  'admin/deleteCategory',
  async (categoryId: number, { rejectWithValue }) => {
    try {
      await categoryService.deleteCategory(categoryId);
      return categoryId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'カテゴリの削除に失敗しました');
    }
  }
);

// カテゴリの有効/無効切り替えアクション
export const toggleCategory = createAsyncThunk(
  'admin/toggleCategory',
  async (categoryId: number, { rejectWithValue }) => {
    try {
      const category = await categoryService.toggleCategory(categoryId);
      return category;
    } catch (error: any) {
      return rejectWithValue(error.message || 'カテゴリの状態変更に失敗しました');
    }
  }
);

// 画像スタイル一覧取得アクション
export const fetchImageStyles = createAsyncThunk(
  'admin/fetchImageStyles',
  async (_, { rejectWithValue }) => {
    try {
      const imageStyles = await adminImageStyleService.getAllStyles();
      return imageStyles;
    } catch (error: any) {
      return rejectWithValue(error.message || '画像スタイル一覧の取得に失敗しました');
    }
  }
);

// 画像スタイル作成アクション
export const createImageStyle = createAsyncThunk(
  'admin/createImageStyle',
  async (styleData: ImageStyleCreateRequest, { rejectWithValue }) => {
    try {
      const imageStyle = await adminImageStyleService.createStyle(styleData);
      return imageStyle;
    } catch (error: any) {
      return rejectWithValue(error.message || '画像スタイルの作成に失敗しました');
    }
  }
);

// 画像スタイル更新アクション
export const updateImageStyle = createAsyncThunk(
  'admin/updateImageStyle',
  async ({ id, styleData }: { id: number; styleData: ImageStyleUpdateRequest }, { rejectWithValue }) => {
    try {
      const imageStyle = await adminImageStyleService.updateStyle(id, styleData);
      return imageStyle;
    } catch (error: any) {
      return rejectWithValue(error.message || '画像スタイルの更新に失敗しました');
    }
  }
);

// 画像スタイル削除アクション
export const deleteImageStyle = createAsyncThunk(
  'admin/deleteImageStyle',
  async (styleId: number, { rejectWithValue }) => {
    try {
      await adminImageStyleService.deleteStyle(styleId);
      return styleId;
    } catch (error: any) {
      return rejectWithValue(error.message || '画像スタイルの削除に失敗しました');
    }
  }
);

// 画像スタイルの有効/無効切り替えアクション
export const toggleImageStyle = createAsyncThunk(
  'admin/toggleImageStyle',
  async (styleId: number, { rejectWithValue }) => {
    try {
      const imageStyle = await adminImageStyleService.toggleStyle(styleId);
      return imageStyle;
    } catch (error: any) {
      return rejectWithValue(error.message || '画像スタイルの状態変更に失敗しました');
    }
  }
);

// プロンプトテンプレート一覧取得アクション
export const fetchImagePromptTemplates = createAsyncThunk(
  'admin/fetchImagePromptTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const templates = await imagePromptTemplateService.getAllTemplates();
      return templates;
    } catch (error: any) {
      return rejectWithValue(error.message || 'プロンプトテンプレート一覧の取得に失敗しました');
    }
  }
);

// プロンプトテンプレート作成アクション
export const createImagePromptTemplate = createAsyncThunk(
  'admin/createImagePromptTemplate',
  async (templateData: ImagePromptTemplateCreateRequest, { rejectWithValue }) => {
    try {
      const template = await imagePromptTemplateService.createTemplate(templateData);
      return template;
    } catch (error: any) {
      return rejectWithValue(error.message || 'プロンプトテンプレートの作成に失敗しました');
    }
  }
);

// プロンプトテンプレート更新アクション
export const updateImagePromptTemplate = createAsyncThunk(
  'admin/updateImagePromptTemplate',
  async ({ id, templateData }: { id: number; templateData: ImagePromptTemplateUpdateRequest }, { rejectWithValue }) => {
    try {
      const template = await imagePromptTemplateService.updateTemplate(id, templateData);
      return template;
    } catch (error: any) {
      return rejectWithValue(error.message || 'プロンプトテンプレートの更新に失敗しました');
    }
  }
);

// プロンプトテンプレート削除アクション
export const deleteImagePromptTemplate = createAsyncThunk(
  'admin/deleteImagePromptTemplate',
  async (templateId: number, { rejectWithValue }) => {
    try {
      await imagePromptTemplateService.deleteTemplate(templateId);
      return templateId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'プロンプトテンプレートの削除に失敗しました');
    }
  }
);

// 動画テンプレート一覧取得アクション
export const fetchVideoTemplates = createAsyncThunk(
  'admin/fetchVideoTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const templates = await adminVideoTemplateService.getAllTemplates();
      return templates;
    } catch (error: any) {
      return rejectWithValue(error.message || '動画テンプレート一覧の取得に失敗しました');
    }
  }
);

// 動画テンプレート作成アクション
export const createVideoTemplate = createAsyncThunk(
  'admin/createVideoTemplate',
  async (templateData: VideoTemplateCreateRequest, { rejectWithValue }) => {
    try {
      const template = await adminVideoTemplateService.createTemplate(templateData);
      return template;
    } catch (error: any) {
      return rejectWithValue(error.message || '動画テンプレートの作成に失敗しました');
    }
  }
);

// 動画テンプレート更新アクション
export const updateVideoTemplate = createAsyncThunk(
  'admin/updateVideoTemplate',
  async ({ id, templateData }: { id: number; templateData: VideoTemplateUpdateRequest }, { rejectWithValue }) => {
    try {
      const template = await adminVideoTemplateService.updateTemplate(id, templateData);
      return template;
    } catch (error: any) {
      return rejectWithValue(error.message || '動画テンプレートの更新に失敗しました');
    }
  }
);

// 動画テンプレート削除アクション
export const deleteVideoTemplate = createAsyncThunk(
  'admin/deleteVideoTemplate',
  async (templateId: number, { rejectWithValue }) => {
    try {
      await adminVideoTemplateService.deleteTemplate(templateId);
      return templateId;
    } catch (error: any) {
      return rejectWithValue(error.message || '動画テンプレートの削除に失敗しました');
    }
  }
);

// 動画テンプレートの有効/無効切り替えアクション
export const toggleVideoTemplate = createAsyncThunk(
  'admin/toggleVideoTemplate',
  async (templateId: number, { rejectWithValue }) => {
    try {
      const template = await adminVideoTemplateService.toggleTemplate(templateId);
      return template;
    } catch (error: any) {
      return rejectWithValue(error.message || '動画テンプレートの状態変更に失敗しました');
    }
  }
);

// プロンプトテンプレート有効化アクション
export const activateImagePromptTemplate = createAsyncThunk(
  'admin/activateImagePromptTemplate',
  async (templateId: number, { rejectWithValue }) => {
    try {
      const template = await imagePromptTemplateService.activateTemplate(templateId);
      return template;
    } catch (error: any) {
      return rejectWithValue(error.message || 'プロンプトテンプレートの有効化に失敗しました');
    }
  }
);

// 管理者スライス
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearAdminError: (state) => {
      state.error = null;
    },
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
    },
    setSelectedAgent: (state, action: PayloadAction<AIAgent | null>) => {
      state.selectedAgent = action.payload;
    },
    setSelectedOrganization: (state, action: PayloadAction<Organization | null>) => {
      state.selectedOrganization = action.payload;
    },
    setSelectedCategory: (state, action: PayloadAction<Category | null>) => {
      state.selectedCategory = action.payload;
    },
    setSelectedImageStyle: (state, action: PayloadAction<AdminImageStyle | null>) => {
      state.selectedImageStyle = action.payload;
    },
    setSelectedImagePromptTemplate: (state, action: PayloadAction<ImagePromptTemplate | null>) => {
      state.selectedImagePromptTemplate = action.payload;
    },
    setSelectedVideoTemplate: (state, action: PayloadAction<AdminVideoTemplate | null>) => {
      state.selectedVideoTemplate = action.payload;
    }
  },
  extraReducers: (builder) => {
    // ユーザー一覧取得処理
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // AIエージェント一覧取得処理
    builder
      .addCase(fetchAgents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agents = action.payload;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 統計情報取得処理
    builder
      .addCase(fetchAdminStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchAdminStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // AIエージェント使用統計取得処理
    builder
      .addCase(fetchAgentUsageStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAgentUsageStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agentUsageStats = action.payload;
      })
      .addCase(fetchAgentUsageStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // ユーザー作成処理
    builder
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users.push(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // ユーザー更新処理
    builder
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        // action.payloadがuserオブジェクトを含む場合とuserオブジェクト自体の場合を処理
        const userData = action.payload.user || action.payload;
        const index = state.users.findIndex(user => user.id === userData.id);
        if (index !== -1) {
          state.users[index] = userData;
        }
        if (state.selectedUser?.id === userData.id) {
          state.selectedUser = userData;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // ユーザー削除処理
    builder
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = state.users.filter(user => user.id !== action.payload);
        if (state.selectedUser?.id === action.payload) {
          state.selectedUser = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // AIエージェント作成処理
    builder
      .addCase(createAgent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAgent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agents.push(action.payload);
      })
      .addCase(createAgent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // AIエージェント更新処理
    builder
      .addCase(updateAgent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAgent.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.agents.findIndex(agent => agent.id === action.payload.id);
        if (index !== -1) {
          state.agents[index] = action.payload;
        }
        if (state.selectedAgent?.id === action.payload.id) {
          state.selectedAgent = action.payload;
        }
      })
      .addCase(updateAgent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // AIエージェント削除処理
    builder
      .addCase(deleteAgent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAgent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.agents = state.agents.filter(agent => agent.id !== action.payload);
        if (state.selectedAgent?.id === action.payload) {
          state.selectedAgent = null;
        }
      })
      .addCase(deleteAgent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
    
    // 組織一覧取得処理
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizations = action.payload;
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 組織詳細取得処理
    builder
      .addCase(fetchOrganizationDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizationDetails = action.payload;
        // 選択中の組織情報も更新
        if (state.selectedOrganization?.id === action.payload.organization.id) {
          state.selectedOrganization = action.payload.organization;
        }
      })
      .addCase(fetchOrganizationDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 組織作成処理
    builder
      .addCase(createOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizations.push(action.payload);
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 組織更新処理
    builder
      .addCase(updateOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.organizations.findIndex(org => org.id === action.payload.id);
        if (index !== -1) {
          state.organizations[index] = action.payload;
        }
        // 選択中の組織が更新対象の場合、選択中の組織情報も更新
        if (state.selectedOrganization?.id === action.payload.id) {
          state.selectedOrganization = action.payload;
        }
        // 詳細情報も更新
        if (state.organizationDetails && state.organizationDetails.organization.id === action.payload.id) {
          state.organizationDetails.organization = action.payload;
        }
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 組織削除処理
    builder
      .addCase(deleteOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizations = state.organizations.filter(org => org.id !== action.payload);
        // 選択中の組織が削除対象の場合、選択をクリア
        if (state.selectedOrganization?.id === action.payload) {
          state.selectedOrganization = null;
          state.organizationDetails = null;
        }
      })
      .addCase(deleteOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 組織メンバー追加・更新処理
    builder
      .addCase(addOrUpdateOrganizationMember.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addOrUpdateOrganizationMember.fulfilled, (state, action) => {
        state.isLoading = false;
        // 招待の場合は、メンバーリストの更新は行わない（招待受諾後に更新される）
        if (action.payload.isInvitation) {
          console.log('addOrUpdateOrganizationMember: 招待が送信されました', action.payload.invitation);
          // 招待の場合は何もしない（メンバーリストの更新は別途fetchOrganizationDetailsで行う）
          return;
        }
        
        // 通常のメンバー追加・更新の場合
        if (state.organizationDetails && state.organizationDetails.organization.id === action.payload.organizationId && action.payload.member) {
          const memberIndex = state.organizationDetails.members.findIndex(m => m.id === action.payload.member.id);
          if (memberIndex !== -1) {
            // 既存メンバーの更新
            state.organizationDetails.members[memberIndex] = action.payload.member;
          } else {
            // 新規メンバーの追加
            state.organizationDetails.members.push(action.payload.member);
          }
        }
      })
      .addCase(addOrUpdateOrganizationMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 組織メンバー削除処理
    builder
      .addCase(removeOrganizationMember.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeOrganizationMember.fulfilled, (state, action) => {
        state.isLoading = false;
        // 組織詳細情報が存在し、かつ対象の組織IDと一致する場合、メンバーリストから削除
        if (state.organizationDetails && state.organizationDetails.organization.id === action.payload.organizationId) {
          state.organizationDetails.members = state.organizationDetails.members.filter(
            member => member.id !== action.payload.userId
          );
        }
      })
      .addCase(removeOrganizationMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 組織メンバーにログイン情報メール送信処理
    builder
      .addCase(sendMemberLoginEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendMemberLoginEmail.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(sendMemberLoginEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 組織のAIエージェント設定更新処理
    builder
      .addCase(updateOrganizationAgentSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrganizationAgentSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        // 組織詳細情報が存在し、かつ対象の組織IDと一致する場合、エージェント設定を更新
        if (state.organizationDetails && state.organizationDetails.organization.id === action.payload.organizationId) {
          // 各エージェントの有効/無効状態を更新
          action.payload.agentSettings.forEach(setting => {
            const agentIndex = state.organizationDetails!.agents.findIndex(a => a.id === setting.agent_id);
            if (agentIndex !== -1) {
              state.organizationDetails!.agents[agentIndex].is_enabled_for_organization = setting.is_enabled;
            }
          });
        }
      })
      .addCase(updateOrganizationAgentSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 組織の有効/無効切り替え処理
    builder
      .addCase(toggleOrganizationActive.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleOrganizationActive.fulfilled, (state, action) => {
        state.isLoading = false;
        // 一覧の組織情報を更新
        const index = state.organizations.findIndex(org => org.id === action.payload.id);
        if (index !== -1) {
          state.organizations[index] = {
            ...state.organizations[index],
            is_active: action.payload.is_active
          };
        }
        
        // 選択中の組織が対象の場合、情報を更新
        if (state.selectedOrganization?.id === action.payload.id) {
          state.selectedOrganization = {
            ...state.selectedOrganization,
            is_active: action.payload.is_active
          };
        }
        
        // 詳細情報も更新
        if (state.organizationDetails && state.organizationDetails.organization.id === action.payload.id) {
          state.organizationDetails.organization = {
            ...state.organizationDetails.organization,
            is_active: action.payload.is_active
          };
        }
      })
      .addCase(toggleOrganizationActive.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 組織使用統計取得処理
    builder
      .addCase(fetchOrganizationUsageStatistics.pending, (state) => {
        state.usageDataLoading = true;
        state.usageDataError = null;
      })
      .addCase(fetchOrganizationUsageStatistics.fulfilled, (state, action) => {
        state.usageDataLoading = false;
        state.organizationUsageData = action.payload;
      })
      .addCase(fetchOrganizationUsageStatistics.rejected, (state, action) => {
        state.usageDataLoading = false;
        state.usageDataError = action.payload as string;
      });

    // カテゴリ一覧取得処理
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // カテゴリ作成処理
    builder
      .addCase(createCategory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories.push(action.payload);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // カテゴリ更新処理
    builder
      .addCase(updateCategory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.categories.findIndex(category => category.id === action.payload.id);
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
        if (state.selectedCategory?.id === action.payload.id) {
          state.selectedCategory = action.payload;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // カテゴリ削除処理
    builder
      .addCase(deleteCategory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories = state.categories.filter(category => category.id !== action.payload);
        if (state.selectedCategory?.id === action.payload) {
          state.selectedCategory = null;
        }
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // カテゴリの有効/無効切り替え処理
    builder
      .addCase(toggleCategory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.categories.findIndex(category => category.id === action.payload.id);
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
        if (state.selectedCategory?.id === action.payload.id) {
          state.selectedCategory = action.payload;
        }
      })
      .addCase(toggleCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 画像スタイル一覧取得処理
    builder
      .addCase(fetchImageStyles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchImageStyles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.imageStyles = action.payload;
      })
      .addCase(fetchImageStyles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 画像スタイル作成処理
    builder
      .addCase(createImageStyle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createImageStyle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.imageStyles.push(action.payload);
      })
      .addCase(createImageStyle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 画像スタイル更新処理
    builder
      .addCase(updateImageStyle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateImageStyle.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.imageStyles.findIndex(style => style.id === action.payload.id);
        if (index !== -1) {
          state.imageStyles[index] = action.payload;
        }
        if (state.selectedImageStyle?.id === action.payload.id) {
          state.selectedImageStyle = action.payload;
        }
      })
      .addCase(updateImageStyle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 画像スタイル削除処理
    builder
      .addCase(deleteImageStyle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteImageStyle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.imageStyles = state.imageStyles.filter(style => style.id !== action.payload);
        if (state.selectedImageStyle?.id === action.payload) {
          state.selectedImageStyle = null;
        }
      })
      .addCase(deleteImageStyle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 画像スタイルの有効/無効切り替え処理
    builder
      .addCase(toggleImageStyle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleImageStyle.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.imageStyles.findIndex(style => style.id === action.payload.id);
        if (index !== -1) {
          state.imageStyles[index] = action.payload;
        }
        if (state.selectedImageStyle?.id === action.payload.id) {
          state.selectedImageStyle = action.payload;
        }
      })
      .addCase(toggleImageStyle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // プロンプトテンプレート一覧取得処理
    builder
      .addCase(fetchImagePromptTemplates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchImagePromptTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.imagePromptTemplates = action.payload;
      })
      .addCase(fetchImagePromptTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // プロンプトテンプレート作成処理
    builder
      .addCase(createImagePromptTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createImagePromptTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.imagePromptTemplates.push(action.payload);
      })
      .addCase(createImagePromptTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // プロンプトテンプレート更新処理
    builder
      .addCase(updateImagePromptTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateImagePromptTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.imagePromptTemplates.findIndex(template => template.id === action.payload.id);
        if (index !== -1) {
          state.imagePromptTemplates[index] = action.payload;
        }
        if (state.selectedImagePromptTemplate?.id === action.payload.id) {
          state.selectedImagePromptTemplate = action.payload;
        }
      })
      .addCase(updateImagePromptTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // プロンプトテンプレート削除処理
    builder
      .addCase(deleteImagePromptTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteImagePromptTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.imagePromptTemplates = state.imagePromptTemplates.filter(template => template.id !== action.payload);
        if (state.selectedImagePromptTemplate?.id === action.payload) {
          state.selectedImagePromptTemplate = null;
        }
      })
      .addCase(deleteImagePromptTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // プロンプトテンプレート有効化処理
    builder
      .addCase(activateImagePromptTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(activateImagePromptTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        // 同じタイプの他のテンプレートをすべて無効化
        state.imagePromptTemplates = state.imagePromptTemplates.map(template => {
          if (template.type === action.payload.type) {
            return {
              ...template,
              is_active: template.id === action.payload.id
            };
          }
          return template;
        });
        if (state.selectedImagePromptTemplate?.id === action.payload.id) {
          state.selectedImagePromptTemplate = action.payload;
        }
      })
      .addCase(activateImagePromptTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 動画テンプレート一覧取得処理
    builder
      .addCase(fetchVideoTemplates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVideoTemplates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.videoTemplates = action.payload;
      })
      .addCase(fetchVideoTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 動画テンプレート作成処理
    builder
      .addCase(createVideoTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createVideoTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.videoTemplates.push(action.payload);
      })
      .addCase(createVideoTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 動画テンプレート更新処理
    builder
      .addCase(updateVideoTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateVideoTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.videoTemplates.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.videoTemplates[index] = action.payload;
        }
        if (state.selectedVideoTemplate?.id === action.payload.id) {
          state.selectedVideoTemplate = action.payload;
        }
      })
      .addCase(updateVideoTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 動画テンプレート削除処理
    builder
      .addCase(deleteVideoTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteVideoTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.videoTemplates = state.videoTemplates.filter(t => t.id !== action.payload);
        if (state.selectedVideoTemplate?.id === action.payload) {
          state.selectedVideoTemplate = null;
        }
      })
      .addCase(deleteVideoTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // 動画テンプレートの有効/無効切り替え処理
    builder
      .addCase(toggleVideoTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleVideoTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.videoTemplates.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.videoTemplates[index] = action.payload;
        }
        if (state.selectedVideoTemplate?.id === action.payload.id) {
          state.selectedVideoTemplate = action.payload;
        }
      })
      .addCase(toggleVideoTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearAdminError, setSelectedUser, setSelectedAgent, setSelectedOrganization, setSelectedCategory, setSelectedImageStyle, setSelectedImagePromptTemplate, setSelectedVideoTemplate } = adminSlice.actions;

export default adminSlice.reducer;
